import { createSlice } from "@reduxjs/toolkit";

const initialState = {
    prompts: {
        data: [],
        loading: false,
        error: null,
        page: 1,
        limit: 1,
        total: 0,
        totalPages: 1,
    },
    promptDetails: null,
    randomPromptID: null,
    categories: {
        data: [],
        loading: false,
        error: null,
        page: 1,
        limit: 1,
        total: 0,
        totalPages: 1,
    },
    dataModals: {
        data: [],
        loading: false,
        error: null,
        page: 1,
        limit: 1,
        total: 0,
        totalPages: 1,
    },
    favouritePrompt: {},
    dataModalReaction: {},
    seenPrompts: {
        data: [],
        loading: false,
        error: null,
        page: 1,
        limit: 1,
        total: 0,
        totalPages: 1,
    },
    unseenPrompts: {
        data: [],
        loading: false,
        error: null,
        page: 1,
        limit: 1,
        total: 0,
        totalPages: 1,
    },
    favouritePrompts: {
        data: [],
        loading: false,
        error: null,
        page: 1,
        limit: 1,
        total: 0,
        totalPages: 1,
    },
    promptsSummary: {
        favourites: [],
        seen: [],
        unseen: [],
        counts: {
            favourites: 0,
            seen: 0,
            unseen: 0
        }
    },
    guestQuota: {
        genericPromptsViewed: 0,
        limit: 5,
        quotaExceeded: false
    },
    promptViews: {
        count: 0,
        seenIds: {},
        loadingSeenIds: false,
        feedbackShown: false,
        checkingFeedback: false,
        hasGivenFeedback: false,
    },
    loading: false,
    error: null,
    activeFeed: null,

};

const promptSlice = createSlice({
    name: "prompt",
    initialState,
    reducers: {
        fetchPromptsStart: (state) => {
            state.prompts.loading = true;
            state.prompts.error = null;
            state.prompts.data = [];
        },
        fetchPromptsSuccess: (state, action) => {
            state.prompts = { ...action.payload, loading: false };
        },
        fetchPromptsFail: (state, action) => {
            state.prompts.loading = false;
            state.prompts.error = action.payload;
        },
        fetchPromptDetailsStart: (state) => {
            state.loading = true;
        },
        fetchPromptDetailsSuccess: (state, action) => {
            state.loading = false;
            const prompt = action.payload;
            state.promptDetails = prompt;
            state.promptsSummary = prompt.summary;
            // Update guest quota if returned from API
            if (prompt.guestQuota) {
                state.guestQuota = prompt.guestQuota;
            }
            if (prompt?._id) {
                state.promptViews.seenIds[prompt._id] = true;
            }
        },
        fetchSeenPromptIdsStart: (state) => {
            state.promptViews.loadingSeenIds = true;
        },
        fetchSeenPromptIdsSuccess: (state, action) => {
            state.promptViews.loadingSeenIds = false;
            const ids = action.payload?.seenPromptIds || [];
            ids.forEach((id) => {
                state.promptViews.seenIds[id] = true;
            });
        },
        fetchSeenPromptIdsFail: (state) => {
            state.promptViews.loadingSeenIds = false;
        },
        fetchPromptDetailsFail: (state, action) => {
            state.loading = false;
            state.error = action.payload;
            state.promptDetails = null;
        },
        clearPromptDetails: (state) => {
            state.promptDetails = null;
            state.error = null;
            state.loading = false;
        },
        fetchRandomPromptIDStart: (state) => {
            state.loading = true;
        },
        fetchRandomPromptIDSuccess: (state, action) => {
            state.loading = false;
            state.randomPromptID = action.payload.promptID;
        },
        fetchRandomPromptIDFail: (state, action) => {
            state.loading = false;
            state.error = action.payload;
        },
        fetchCategoriesStart: (state, action) => {
            state.categories.loading = true;
            state.categories.error = null;
            if (action.payload?.page === 1) {
                state.categories.data = [];
            }
        },
        fetchCategoriesSuccess: (state, action) => {
            state.categories = { ...action.payload, loading: false };
        },
        fetchCategoriesFail: (state, action) => {
            state.categories.loading = false;
            state.categories.error = action.payload;
        },
        fetchDataModalsStart: (state) => {
            state.loading = true;
        },
        fetchDataModalsSuccess: (state, action) => {
            state.loading = false;
            state.dataModals = action.payload;
        },
        fetchDataModalsFail: (state, action) => {
            state.loading = false;
            state.error = action.payload;
        },
        favouritePromptStart: (state) => {
            state.loading = true;
        },
        favouritePromptSuccess: (state, action) => {
            state.loading = false;
            const { interaction: payload, summary } = action.payload;
            state.promptsSummary = summary;
            if (!payload && state.promptDetails) {
                state.promptDetails.isFavourited = false;
                return;
            }
            const { promptID, favourite } = payload || {};
            if (state.promptDetails && state.promptDetails._id === promptID) {
                state.promptDetails.isFavourited = favourite;
            }
        },
        favouritePromptFail: (state, action) => {
            state.loading = false;
            state.error = action.payload;
        },
        reactToDataModalStart: (state) => {
            state.loading = true;
        },
        reactToDataModalSuccess: (state, action) => {
            state.loading = false;
            const { dataModalID, reaction } = action.payload;
            if (state.promptDetails?.dataModals?.length) {
                const modal = state.promptDetails.dataModals.find(dm => dm._id === dataModalID);
                if (modal) {
                    modal.reaction = reaction;
                }
            }
        },
        reactToDataModalFail: (state, action) => {
            state.loading = false;
            state.error = action.payload;
        },
        fetchSeenPromptsStart: (state) => {
            state.loading = true;
            state.error = null;
        },
        fetchSeenPromptsSuccess: (state, action) => {
            state.loading = false;
            state.seenPrompts.loading = false;
            state.seenPrompts.error = null;

            const { data, totalCount, page, totalPages } = action.payload;

            state.seenPrompts.data = page === 1 ? data : [...state.seenPrompts.data, ...data];
            state.seenPrompts.page = page;
            state.seenPrompts.total = totalCount;
            state.seenPrompts.totalPages = totalPages;
        },
        fetchSeenPromptsFail: (state, action) => {
            state.loading = false;
            state.error = action.payload;
        },

        fetchUnseenPromptsStart: (state) => {
            state.loading = true;
            state.error = null;
        },
        fetchUnseenPromptsSuccess: (state, action) => {
            state.loading = false;
            state.unseenPrompts.loading = false;
            state.unseenPrompts.error = null;

            const { data, totalCount, page, totalPages } = action.payload;

            state.unseenPrompts.data = page === 1 ? data : [...state.unseenPrompts.data, ...data];
            state.unseenPrompts.page = page;
            state.unseenPrompts.total = totalCount;
            state.unseenPrompts.totalPages = totalPages;
        },
        fetchUnseenPromptsFail: (state, action) => {
            state.loading = false;
            state.error = action.payload;
        },

        fetchFavouritePromptsStart: (state) => {
            state.loading = true;
            state.error = null;
        },
        fetchFavouritePromptsSuccess: (state, action) => {
            state.loading = false;
            state.favouritePrompts.loading = false;
            state.favouritePrompts.error = null;
            const { data, totalCount, page, totalPages } = action.payload;
            state.favouritePrompts.data = page === 1 ? data : [...state.favouritePrompts.data, ...data];
            state.favouritePrompts.page = page;
            state.favouritePrompts.total = totalCount;
            state.favouritePrompts.totalPages = totalPages;
        },
        fetchFavouritePromptsFail: (state, action) => {
            state.loading = false;
            state.error = action.payload;
        },

        fetchPromptsSummaryStart: (state) => {
            state.loading = true;
            state.error = null;
        },
        fetchPromptsSummarySuccess: (state, action) => {
            state.loading = false;
            state.promptsSummary = action.payload;
        },
        fetchPromptsSummaryFail: (state, action) => {
            state.loading = false;
            state.error = action.payload;
        },
        setActiveFeed(state, { payload }) {
            state.activeFeed = payload || null;
        },
        resetGuestQuota(state) {
            state.guestQuota = {
                genericPromptsViewed: 0,
                limit: 5,
                quotaExceeded: false
            };
        },
        setGuestQuotaExceeded(state, action) {
            // Called when backend returns 403 quota exceeded
            const { genericPromptsViewed, limit } = action.payload || {};
            state.guestQuota = {
                genericPromptsViewed: genericPromptsViewed || state.guestQuota.genericPromptsViewed,
                limit: limit || state.guestQuota.limit,
                quotaExceeded: true
            };
        },
        trackPromptView(state, { payload }) {
            const id = payload?.promptID;
            if (!id) return;
            if (state.promptViews.seenIds[id]) return;
            state.promptViews.seenIds[id] = true;
            state.promptViews.count += 1;
        },
        markPromptFeedbackShown(state) {
            state.promptViews.feedbackShown = true;
        },
        checkUserFeedbackStart: (state) => {
            state.promptViews.checkingFeedback = true;
        },
        checkUserFeedbackSuccess: (state, action) => {
            state.promptViews.checkingFeedback = false;
            state.promptViews.hasGivenFeedback = action.payload.hasGivenFeedback;
        },
        checkUserFeedbackFail: (state) => {
            state.promptViews.checkingFeedback = false;
        },
    },
});

export const {
    fetchPromptsStart,
    fetchPromptsSuccess,
    fetchPromptsFail,
    fetchPromptDetailsStart,
    fetchPromptDetailsSuccess,
    fetchPromptDetailsFail,
    clearPromptDetails,
    fetchRandomPromptIDStart,
    fetchRandomPromptIDSuccess,
    fetchRandomPromptIDFail,
    fetchCategoriesStart,
    fetchCategoriesSuccess,
    fetchCategoriesFail,
    fetchDataModalsStart,
    fetchDataModalsSuccess,
    fetchDataModalsFail,
    favouritePromptStart,
    favouritePromptSuccess,
    favouritePromptFail,
    reactToDataModalStart,
    reactToDataModalSuccess,
    reactToDataModalFail,
    fetchSeenPromptsStart,
    fetchSeenPromptsSuccess,
    fetchSeenPromptsFail,
    fetchSeenPromptIdsStart,
    fetchSeenPromptIdsSuccess,
    fetchSeenPromptIdsFail,

    fetchUnseenPromptsStart,
    fetchUnseenPromptsSuccess,
    fetchUnseenPromptsFail,

    fetchFavouritePromptsStart,
    fetchFavouritePromptsSuccess,
    fetchFavouritePromptsFail,
    fetchPromptsSummaryStart,
    fetchPromptsSummarySuccess,
    fetchPromptsSummaryFail,
    setActiveFeed,
    resetGuestQuota,
    setGuestQuotaExceeded,
    trackPromptView,
    markPromptFeedbackShown,
    checkUserFeedbackStart,
    checkUserFeedbackSuccess,
    checkUserFeedbackFail,
} = promptSlice.actions;

export default promptSlice.reducer;